import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaYoutube } from "react-icons/fa";
import "./Footer.css";

import logo from "../../../assets/images/vucalogo.svg";
import orangeLogo from "../../../assets/images/logo.png";

import fb from "../../../assets/icons/social1.svg";
import ig from "../../../assets/icons/social2.svg";
import li from "../../../assets/icons/social3.svg";
import { getPageContent } from "../../../api/content";
import { subscribeEmail } from "../../../api/subscribe";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const extractSubscribeError = (error) => {
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) return data.message;
    const errs = data.errors;
    if (errs && typeof errs === "object") {
      for (const key of ["Email", "email"]) {
        const list = errs[key];
        if (Array.isArray(list) && typeof list[0] === "string" && list[0].trim()) {
          return list[0];
        }
      }
      const firstList = Object.values(errs).find(
        (v) => Array.isArray(v) && typeof v[0] === "string" && v[0].trim()
      );
      if (firstList) return firstList[0];
    }
  }
  return "Subscription failed. Please try again.";
};

const FALLBACK = {
  newsletterTitle: "Stay up to date",
  newsletterTitleAccent: "with our newsletter",
  newsletterPlaceholder: "Enter your email",
  newsletterButton: "Subscribe",
  description:
    "Iraq's leading gypsum manufacturer.\nBuilding the future of construction.",
  location: "Baghdad & Erbil, Iraq",
  email: "info@irqgyp.com",
  website: "irqgyp.com",
  quickLinksTitle: "QUICK LINKS",
  companyTitle: "COMPANY",
  copyrightTemplate: "© {year} IRQGYP. All rights reserved.",
  privacyLabel: "Privacy Policy",
  termsLabel: "Terms of Use",
  sitemapLabel: "Sitemap",
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const tryParseJsonObject = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const unwrapSectionData = (section) => {
  if (section == null) return section;
  if (typeof section === "string") {
    const parsed = tryParseJsonObject(section);
    return parsed ? unwrapSectionData(parsed) : section;
  }
  if (typeof section !== "object") return section;
  if (section.data && typeof section.data === "object") return section.data;
  return section;
};

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const coalesceFooterPayload = (section) => {
  const direct = unwrapSectionData(section);
  const payload = direct && typeof direct === "object" ? { ...direct } : {};
  const rawValue = section?.value ?? direct?.value;

  if (rawValue && typeof rawValue === "object") Object.assign(payload, rawValue);
  if (typeof rawValue === "string") {
    const parsed = tryParseJsonObject(rawValue);
    if (parsed) Object.assign(payload, parsed);
  }

  return payload;
};

const pickString = (obj, keys, fallback = "") => {
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return fallback;
};

const Footer = () => {
    const { i18n } = useTranslation();
    const [footerContent, setFooterContent] = useState({});
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState({ type: "", message: "" });

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const trimmed = email.trim();
        if (!trimmed) {
            setFeedback({ type: "error", message: "Please enter your email." });
            return;
        }
        if (!EMAIL_REGEX.test(trimmed)) {
            setFeedback({
                type: "error",
                message: "Please enter a valid email address.",
            });
            return;
        }
        setSubmitting(true);
        setFeedback({ type: "", message: "" });
        try {
            await subscribeEmail({ email: trimmed });
            setEmail("");
            setFeedback({
                type: "success",
                message: "Thanks for subscribing! We'll be in touch.",
            });
        } catch (error) {
            setFeedback({ type: "error", message: extractSubscribeError(error) });
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        let isMounted = true;
        const loadFooterContent = async () => {
            try {
                const data = await getPageContent({
                    page: "shared",
                    language: String(i18n.language || "en").toLowerCase(),
                });
                if (isMounted) setFooterContent(data || {});
            } catch {
                if (isMounted) setFooterContent({});
            }
        };
        loadFooterContent();
        return () => {
            isMounted = false;
        };
    }, [i18n.language]);

    const view = useMemo(() => {
        const payload = coalesceFooterPayload(findPageSection(footerContent, "footer"));
        const year = new Date().getFullYear();
        const copyrightRaw = pickString(
            payload,
            ["copyright", "copyrightText", "bottomText"],
            FALLBACK.copyrightTemplate
        );
        return {
            newsletterTitle: pickString(
                payload,
                ["newsletterTitle", "title", "titleLine1"],
                FALLBACK.newsletterTitle
            ),
            newsletterTitleAccent: pickString(
                payload,
                ["newsletterTitleAccent", "titleAccent", "titleLine2"],
                FALLBACK.newsletterTitleAccent
            ),
            newsletterPlaceholder: pickString(
                payload,
                ["newsletterPlaceholder", "emailPlaceholder"],
                FALLBACK.newsletterPlaceholder
            ),
            newsletterButton: pickString(
                payload,
                ["newsletterButton", "subscribeButton", "buttonText"],
                FALLBACK.newsletterButton
            ),
            description: pickString(
                payload,
                ["description", "companyDescription", "leftDescription"],
                FALLBACK.description
            ),
            location: pickString(payload, ["location", "address"], FALLBACK.location),
            email: pickString(payload, ["email", "contactEmail"], FALLBACK.email),
            website: pickString(payload, ["website", "site"], FALLBACK.website),
            quickLinksTitle: pickString(
                payload,
                ["quickLinksTitle", "linksTitle"],
                FALLBACK.quickLinksTitle
            ),
            companyTitle: pickString(payload, ["companyTitle"], FALLBACK.companyTitle),
            copyrightText: copyrightRaw.replace(/\{year\}/gi, String(year)),
            privacyLabel: pickString(payload, ["privacyLabel"], FALLBACK.privacyLabel),
            termsLabel: pickString(payload, ["termsLabel"], FALLBACK.termsLabel),
            sitemapLabel: pickString(payload, ["sitemapLabel"], FALLBACK.sitemapLabel),
        };
    }, [footerContent]);

    const descriptionLines = String(view.description || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    return (
        <footer className="footer">
            <div className="footer-top">
                <div className="footer-container footer-newsletter">
                    <h2>
                        {view.newsletterTitle} <span>{view.newsletterTitleAccent}</span>
                    </h2>

                    <form
                        className="newsletter-input"
                        onSubmit={handleSubscribe}
                        noValidate
                    >
                        <input
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder={view.newsletterPlaceholder}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (feedback.message) setFeedback({ type: "", message: "" });
                            }}
                            disabled={submitting}
                            aria-label={view.newsletterPlaceholder}
                        />
                        <button type="submit" disabled={submitting}>
                            {submitting ? "Subscribing..." : view.newsletterButton}
                        </button>
                    </form>
                    {feedback.message ? (
                        <p
                            className={`newsletter-feedback newsletter-feedback--${feedback.type}`}
                            role={feedback.type === "error" ? "alert" : "status"}
                        >
                            {feedback.message}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="footer-container footer-main">
                <div className="footer-left">


                    <p>
                        {descriptionLines.map((line, idx) => (
                            <React.Fragment key={`${line}-${idx}`}>
                                {line}
                                {idx < descriptionLines.length - 1 ? <br /> : null}
                            </React.Fragment>
                        ))}
                    </p>

                    <div className="line" />

                    <span>{view.location}</span>
                    <span>{view.email}</span>
                    <span>{view.website}</span>

                    <div className="socials">
                        <a href="https://www.facebook.com/irqgyp" target="_blank" rel="noopener noreferrer">
                            <img src={fb} alt="Facebook" />
                        </a>
                        <a href="https://www.instagram.com/irqgyp" target="_blank" rel="noopener noreferrer">
                            <img src={ig} alt="Instagram" />
                        </a>
                        <a href="https://www.linkedin.com/company/irqgyp" target="_blank" rel="noopener noreferrer">
                            <img src={li} alt="LinkedIn" />
                        </a>
                        <a href="https://www.youtube.com/@irqgyp" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="social-icon youtube-icon">
                                <FaYoutube />
                        </a>
                    </div>
                </div>

                <div className="footer-links">
                    <div>
                        <h4>{view.quickLinksTitle}</h4>
                        <ul>
                            <li>
                                <Link to="/allsolutions" className="footer-link">
                                    Solutions
                                </Link>
                            </li>
                            <li>
                                <Link to="/products" className="footer-link">
                                    Products
                                </Link>
                            </li>
                            <li>
                                <Link to="/allprojects" className="footer-link">
                                    Projects
                                </Link>
                            </li>
                            <li>
                                <Link to="/services" className="footer-link">
                                    Services
                                </Link>
                            </li>
                            <li>
                                <Link to="/sectors" className="footer-link">
                                    Sectors
                                </Link>
                            </li>
                            <li>
                                <Link to="/estimator" className="footer-link">
                                    Estimator
                                </Link>
                            </li>
                            <li>
                                <Link to="/blog" className="footer-link">
                                    Blog
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4>{view.companyTitle}</h4>
                        <ul>
                            <li>
                                <Link to="/about" className="footer-link">
                                    About Us
                                </Link>
                            </li>
                            <li>
                                <Link to="/downloads" className="footer-link">
                                    Downloads
                                </Link>
                            </li>
                            <li>
                                <Link to="/academy" className="footer-link">
                                    Academy
                                </Link>
                            </li>
                            <li>
                                <Link to="/contactus" className="footer-link">
                                    Contact Us
                                </Link>
                            </li>
                            <li>
                                <Link to="/downloads" className="footer-link">
                                    Download profile
                                </Link>
                            </li>
                            <li>
                                <Link to="/downloads" className="footer-link">
                                    IRQGYP Technical Annual
                                </Link>
                            </li>
                            <li>
                                <Link to="/careers" className="footer-link">
                                    Careers
                                </Link>
                            </li>

                        </ul>
                    </div>
                </div>

                <div className="footer-right">
                    <img src={orangeLogo} alt="big logo" />
                </div>
            </div>

            <div className="footer-bottom">
                <div className="footer-container footer-bottom-content">
                    <span>{view.copyrightText}</span>

                    <div className="footer-bottom-links">
                        <Link to="/terms" className="footer-link">
                            {view.privacyLabel}
                        </Link>
                        <Link to="/terms" className="footer-link">
                            {view.termsLabel}
                        </Link>
                        <Link to="/" className="footer-link">
                            {view.sitemapLabel}
                        </Link>
                        <a href="https://vuca.li/" target="_blank" rel="noopener noreferrer">
                            <img src={logo} alt="logo" className="footer-logo" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;